import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import './Home.css';

const AELF_URL_PREFIX = 'https://www.aelf.org/';

function Home() {
  const [languages, setLanguages] = useState([]);
  const [sourceLang, setSourceLang] = useState('de');
  const [targetLang, setTargetLang] = useState('fr');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [gptModel, setGptModel] = useState('gpt-4o-mini');
  const [transcriptionModel, setTranscriptionModel] = useState('gpt-4o-transcribe');
  const [context, setContext] = useState('');
  const [sundayMassEnabled, setSundayMassEnabled] = useState(false);
  const [massReadingsUrl, setMassReadingsUrl] = useState('');
  const [loadingMass, setLoadingMass] = useState(false);
  const [massError, setMassError] = useState('');
  const lastFetchedMassUrlRef = useRef('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/languages`);
      setLanguages(response.data.languages);
    } catch (err) {
      console.error('Error fetching languages:', err);
      setError('Failed to load languages');
    }
  };

  const SUNDAY_MASS_INTRO = 'The transcript and translation are based on the celebration of a Catholic mass.';

  const fetchMassReadings = async (customUrl = null) => {
    setLoadingMass(true);
    setMassError('');
    try {
      const params = customUrl ? { url: customUrl } : {};
      const response = await axios.get(`${API_BASE_URL}/mass-readings`, { params });
      const { url, text } = response.data;
      lastFetchedMassUrlRef.current = url;
      setMassReadingsUrl(url);
      setContext(
        SUNDAY_MASS_INTRO + '\n\nReadings of the day (AELF ' + url + '):\n\n' + (text || '')
      );
      setShowAdvanced(true);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to fetch mass readings';
      setMassError(msg);
    } finally {
      setLoadingMass(false);
    }
  };

  const onSundayMassChange = (checked) => {
    setSundayMassEnabled(checked);
    if (checked) fetchMassReadings();
    else {
      setContext('');
      setMassReadingsUrl('');
      setMassError('');
      lastFetchedMassUrlRef.current = '';
    }
  };

  useEffect(() => {
    if (!sundayMassEnabled) return;
    const url = massReadingsUrl.trim();
    if (!url || !url.startsWith(AELF_URL_PREFIX) || url === lastFetchedMassUrlRef.current) return;
    const t = setTimeout(() => {
      if (massReadingsUrl.trim().startsWith(AELF_URL_PREFIX) && massReadingsUrl.trim() !== lastFetchedMassUrlRef.current) {
        fetchMassReadings(massReadingsUrl.trim());
      }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sundayMassEnabled, massReadingsUrl]);

  const createSession = async (type) => {
    if (type === 'viewer') {
      navigate('/viewer');
      return;
    }
    if (sourceLang === targetLang) {
      setError('Please select different source and target languages');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/sessions`, {
        source_lang: sourceLang,
        target_lang: targetLang,
        gpt_model: gptModel,
        transcription_model: transcriptionModel,
        context: context.trim() || null,
      });

      const sessionId = response.data.session_id;
      navigate(`/capture/${sessionId}`);
    } catch (err) {
      console.error('Error creating session:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create session';
      setError(`Failed to create session: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home-container">
      <div className="home-card">
        <h1 className="title">🌐 Live Translator</h1>
        <p className="subtitle">Real-time audio translation</p>

        {error && <div className="error-message">{error}</div>}

        <div className="form-section">
          <div className="form-group">
            <label htmlFor="source-lang">Source Language</label>
            <select
              id="source-lang"
              value={sourceLang}
              onChange={(e) => setSourceLang(e.target.value)}
              className="select-input"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div className="arrow-icon">→</div>

          <div className="form-group">
            <label htmlFor="target-lang">Target Language</label>
            <select
              id="target-lang"
              value={targetLang}
              onChange={(e) => setTargetLang(e.target.value)}
              className="select-input"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="preset-section preset-sunday-mass">
          <label className="preset-label">
            <input
              type="checkbox"
              checked={sundayMassEnabled}
              onChange={(e) => onSundayMassChange(e.target.checked)}
              disabled={loadingMass}
            />
            <span className="preset-title">🙏 Sunday Mass</span>
          </label>
          <p className="preset-hint">
            Sets context for a Catholic mass. Readings are fetched from <a href="https://www.aelf.org" target="_blank" rel="noopener noreferrer">AELF</a> for the closest Sunday.
          </p>
          {sundayMassEnabled && (
            <div className="preset-options">
              <label className="preset-url-label">Or paste another AELF URL (e.g. another date); the page will be fetched automatically.</label>
              <input
                type="url"
                value={massReadingsUrl}
                onChange={(e) => setMassReadingsUrl(e.target.value)}
                placeholder="https://www.aelf.org/2026-01-25/romain/messe"
                className="preset-url-input preset-url-input-full"
              />
              {loadingMass && <p className="preset-loading">⏳ Fetching readings…</p>}
              {massError && <p className="preset-error">{massError}</p>}
            </div>
          )}
        </div>

        <div className="advanced-toggle">
          <button 
            className="toggle-advanced-btn"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? '▼ Hide Advanced Settings' : '▶ Show Advanced Settings'}
          </button>
        </div>

        {showAdvanced && (
          <div className="advanced-settings">
            <h3>⚙️ Advanced Settings</h3>
            
            <div className="form-group">
              <label htmlFor="transcription-model">🎤 Transcription Model</label>
              <select
                id="transcription-model"
                value={transcriptionModel}
                onChange={(e) => setTranscriptionModel(e.target.value)}
                className="select-input"
              >
                <option value="whisper-1">Whisper-1 ($0.006/min) ⭐ Standard</option>
                <option value="gpt-4o-transcribe">GPT-4o-transcribe ($0.015/min) 🚀 Premium</option>
              </select>
              <small className="hint">GPT-4o-transcribe offers higher accuracy for complex audio</small>
            </div>

            <div className="form-group">
              <label htmlFor="gpt-model">🌍 Translation Model</label>
                <select
                  id="gpt-model"
                  value={gptModel}
                  onChange={(e) => setGptModel(e.target.value)}
                  className="select-input"
                >
                  <option value="gpt-4o">GPT-4o (~$0.015/min) ⭐ Best Quality</option>
                  <option value="gpt-4o-mini">GPT-4o Mini (~$0.003/min) 💰 Best Value</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo (~$0.010/min) Fast</option>
                  <option value="gpt-4">GPT-4 (~$0.030/min) Premium</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo (~$0.002/min) Budget</option>
                </select>
                <small className="hint">Higher quality = better natural language & context understanding</small>
            </div>

            <div className="form-group">
              <label htmlFor="context">Context (Optional)</label>
              <textarea
                id="context"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                className="textarea-input"
                placeholder="e.g., 'Religious sermon', 'Medical conference', 'Business meeting', 'Technical presentation'..."
                rows="3"
              />
              <small className="hint">Provide context to improve translation accuracy for specialized vocabulary</small>
            </div>

            <div className="pricing-note">
              💡 <strong>Total cost:</strong> Selected transcription model + Selected GPT model
              <br/>
              ⭐ <strong>Best quality:</strong> GPT-4o-transcribe + GPT-4o (~$0.030/min total)
              <br/>
              💰 <strong>Best value:</strong> Whisper-1 + GPT-4o-mini (~$0.009/min total)
            </div>
          </div>
        )}

        <div className="button-group">
          <button
            className="btn btn-primary"
            onClick={() => createSession('capture')}
            disabled={loading}
          >
            {loading ? '⏳ Creating...' : '🎤 Start Capturing'}
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => createSession('viewer')}
            disabled={loading}
          >
            {loading ? '⏳ Creating...' : '👁 View Translation'}
          </button>
        </div>

      </div>
    </div>
  );
}

export default Home;
